// =============================================================================
// Who appears in a brand's generated images.
//
// A shop in Accra advertising to Ghanaians should not get stock photography of
// people who look nothing like its customers, and nor should a shop in Osaka
// or Lima. Image models drift towards whatever dominates their training data
// unless told otherwise, so "say nothing" is not neutral — it is a choice, and
// usually the wrong one outside Europe and North America.
//
// The default is therefore `local`: match the brand's own country, which the
// brand already tells us. Everything else is an override for a business whose
// customers are not the local average.
//
// This describes who the marketing is for. It is a localisation setting in the
// same family as language and dialect, and it belongs beside them.
// =============================================================================
export const IMAGE_PEOPLE_OPTIONS = [
    "local",
    "black",
    "white",
    "east_asian",
    "south_asian",
    "southeast_asian",
    "middle_eastern",
    "hispanic_latino",
    "mixed",
    "any",
    "none",
];
/**
 * The English name of a country code, or null if it does not name one.
 *
 * `Intl.DisplayNames` does not throw on nonsense: given "ZZ" it answers
 * "Unknown Region", and some engines hand the code straight back. Either
 * would end up in a prompt as "people local to Unknown Region", which is
 * worse than no instruction — so both are treated as "no answer".
 */
function regionName(countryCode) {
    try {
        const name = new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode);
        if (!name || name === countryCode || /unknown/i.test(name))
            return null;
        return name;
    }
    catch {
        return null;
    }
}
/**
 * The sentence appended to an image prompt, or null for "no instruction".
 *
 * `any` returns null rather than a sentence saying "anyone": an instruction to
 * be unspecific is still an instruction, and the shortest way to leave the
 * model alone is to say nothing.
 *
 * `local` leans on the country rather than a hardcoded table of which
 * ethnicities live where. Image models know what people in a named country
 * look like, that answer stays right as a brand changes market, and it avoids
 * this file asserting anything about a nation's make-up.
 */
export function describeImagePeople(option, countryCode) {
    switch (option ?? "local") {
        case "any":
            return null;
        case "none":
            return "Show no people at all. Feature only the product, the food or the setting.";
        case "local": {
            const region = regionName(countryCode);
            // Nothing sensible to say, so say nothing. A prompt is better off
            // unconstrained than constrained by a placeholder.
            return region ? `Any people shown should look local to ${region}.` : null;
        }
        case "black":
            return "Any people shown should be Black.";
        case "white":
            return "Any people shown should be White.";
        case "east_asian":
            return "Any people shown should be East Asian.";
        case "south_asian":
            return "Any people shown should be South Asian.";
        case "southeast_asian":
            return "Any people shown should be Southeast Asian.";
        case "middle_eastern":
            return "Any people shown should be Middle Eastern.";
        case "hispanic_latino":
            return "Any people shown should be Hispanic or Latino.";
        case "mixed":
            return "People shown should be a mix of ethnicities.";
    }
}
//# sourceMappingURL=imagePeople.js.map